/**
 * Audioguía producer — one guide, every language, chapter by chapter.
 *
 * AI is invoked explicitly per chapter (draft script / generate MP3) and the
 * result is always reviewed by a human before publishing.
 */
import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Loader2,
  Mic,
  Plus,
  Save,
  Sparkles,
  Trash2,
  Upload,
  Volume2,
} from "lucide-react";

import { AdminNav } from "@/components/admin/AdminNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  getAudioGuideAdmin,
  updateAudioGuide,
  addGuideLanguage,
  updateGuideLanguage,
  deleteGuideLanguage,
  upsertChapter,
  deleteChapter,
  reorderChapters,
  generateChapterScript,
  generateChapterAudio,
  getChapterAudioUrl,
  uploadChapterAudio,
  deleteChapterAudio,
  type AdminChapter,
} from "@/lib/audio-guides-admin.functions";
import {
  AUDIO_GUIDE_LANGS,
  EXPERIENCE_TYPES,
  EXPERIENCE_TYPE_LABELS,
  LANG_LABELS,
  formatDuration,
  type AudioGuideLang,
  type ExperienceType,
} from "@/lib/audio-guide-templates";

export const Route = createFileRoute("/_authenticated/admin/audioguias/$id")({
  head: () => ({
    meta: [
      { title: "Audioguía · Editor" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: Page,
});

function Page() {
  const { id } = Route.useParams();
  const qc = useQueryClient();

  const getFn = useServerFn(getAudioGuideAdmin);
  const updateGuideFn = useServerFn(updateAudioGuide);
  const addLangFn = useServerFn(addGuideLanguage);
  const updateLangFn = useServerFn(updateGuideLanguage);
  const deleteLangFn = useServerFn(deleteGuideLanguage);
  const upsertChapterFn = useServerFn(upsertChapter);
  const deleteChapterFn = useServerFn(deleteChapter);
  const reorderFn = useServerFn(reorderChapters);
  const scriptFn = useServerFn(generateChapterScript);
  const audioFn = useServerFn(generateChapterAudio);
  const urlFn = useServerFn(getChapterAudioUrl);
  const uploadFn = useServerFn(uploadChapterAudio);
  const deleteAudioFn = useServerFn(deleteChapterAudio);

  const [lang, setLang] = useState<AudioGuideLang | null>(null);
  const [notice, setNotice] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [busyChapter, setBusyChapter] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ id: string; url: string } | null>(null);

  const guideQuery = useQuery({
    queryKey: ["admin", "audioguide", id, lang],
    queryFn: () => getFn({ data: { guideId: id, ...(lang ? { lang } : {}) } }),
  });

  useEffect(() => {
    if (!lang && guideQuery.data?.lang) setLang(guideQuery.data.lang);
  }, [guideQuery.data?.lang, lang]);

  const guide = guideQuery.data?.guide ?? null;
  const chapters = guideQuery.data?.chapters ?? [];
  const currentLangRow = useMemo(
    () => guide?.languages.find((l) => l.lang === (lang ?? guideQuery.data?.lang)) ?? null,
    [guide, lang, guideQuery.data?.lang],
  );

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin", "audioguide", id] });
  const ok = (text: string) => setNotice({ kind: "ok", text });
  const fail = (e: unknown) =>
    setNotice({ kind: "err", text: e instanceof Error ? e.message : "Acción no completada" });

  const run = async (fn: () => Promise<unknown>, message: string, chapterId?: string) => {
    if (chapterId) setBusyChapter(chapterId);
    try {
      await fn();
      ok(message);
      await refresh();
    } catch (e) {
      fail(e);
    } finally {
      setBusyChapter(null);
    }
  };

  if (guideQuery.isLoading || !guide) {
    return (
      <main className="mx-auto w-full max-w-5xl px-4 py-8">
        <AdminNav />
        <p className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando audioguía…
        </p>
      </main>
    );
  }

  const missingLangs = AUDIO_GUIDE_LANGS.filter(
    (l) => !guide.languages.some((x) => x.lang === l),
  );

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-8">
      <AdminNav />

      <div className="mt-6 flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/admin/audioguias">
            <ArrowLeft className="mr-1.5 h-4 w-4" /> Catálogo
          </Link>
        </Button>
        {currentLangRow?.status === "published" && (
          <Button variant="outline" size="sm" asChild>
            <a
              href={`/audioguia/${guide.tourSlug}?lang=${currentLangRow.lang}`}
              target="_blank"
              rel="noreferrer"
            >
              Ver como turista
            </a>
          </Button>
        )}
      </div>

      <header className="mt-4">
        <h1 className="text-2xl font-black text-foreground">
          {guide.tourTitle ?? guide.tourSlug}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {guide.tourSlug} · {guide.tourSource === "bokun" ? "Bokun" : "Propio"}
          {guide.destination ? ` · ${guide.destination}` : ""}
        </p>
      </header>

      {notice && (
        <p
          className={`mt-4 text-sm ${notice.kind === "ok" ? "text-primary" : "text-destructive"}`}
        >
          {notice.text}
        </p>
      )}

      {/* Guide settings */}
      <section className="mt-6 rounded-2xl border border-border bg-card p-5">
        <h2 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">
          Configuración
        </h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className="text-sm">
            <span className="text-xs font-medium text-muted-foreground">Tipo de experiencia</span>
            <select
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={guide.experienceType}
              onChange={(e) =>
                void run(
                  () =>
                    updateGuideFn({
                      data: {
                        guideId: guide.id,
                        experienceType: e.currentTarget.value as ExperienceType,
                      },
                    }),
                  "Tipo actualizado",
                )
              }
            >
              {EXPERIENCE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {EXPERIENCE_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm">
            <span className="text-xs font-medium text-muted-foreground">Destino</span>
            <Input
              className="mt-1"
              defaultValue={guide.destination ?? ""}
              onBlur={(e) =>
                void run(
                  () =>
                    updateGuideFn({
                      data: { guideId: guide.id, destination: e.currentTarget.value || null },
                    }),
                  "Destino actualizado",
                )
              }
            />
          </label>

          <label className="text-sm">
            <span className="text-xs font-medium text-muted-foreground">Imagen de portada (URL)</span>
            <Input
              className="mt-1"
              defaultValue={guide.coverImageUrl ?? ""}
              onBlur={(e) =>
                void run(
                  () =>
                    updateGuideFn({
                      data: { guideId: guide.id, coverImageUrl: e.currentTarget.value || null },
                    }),
                  "Portada actualizada",
                )
              }
            />
          </label>

          <label className="text-sm">
            <span className="text-xs font-medium text-muted-foreground">Prioridad</span>
            <Input
              className="mt-1"
              type="number"
              min={0}
              max={1000}
              defaultValue={guide.priority}
              onBlur={(e) =>
                void run(
                  () =>
                    updateGuideFn({
                      data: {
                        guideId: guide.id,
                        priority: Number(e.currentTarget.value) || 0,
                      },
                    }),
                  "Prioridad actualizada",
                )
              }
            />
          </label>

          <label className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2 text-sm sm:col-span-2">
            <span>
              Mostrar “Muy pronto” en la página del tour
              <span className="block text-xs text-muted-foreground">
                Útil mientras se produce el contenido.
              </span>
            </span>
            <Switch
              checked={guide.comingSoon}
              onCheckedChange={(v) =>
                void run(
                  () => updateGuideFn({ data: { guideId: guide.id, comingSoon: v } }),
                  "Actualizado",
                )
              }
            />
          </label>

          <label className="text-sm sm:col-span-2">
            <span className="text-xs font-medium text-muted-foreground">Notas internas</span>
            <Textarea
              className="mt-1"
              rows={2}
              defaultValue={guide.notes ?? ""}
              onBlur={(e) =>
                void run(
                  () =>
                    updateGuideFn({
                      data: { guideId: guide.id, notes: e.currentTarget.value || null },
                    }),
                  "Notas guardadas",
                )
              }
            />
          </label>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">Estado de la audioguía:</span>
          {(["preparing", "published", "disabled"] as const).map((s) => (
            <Button
              key={s}
              size="sm"
              variant={guide.status === s ? "default" : "outline"}
              onClick={() =>
                void run(
                  () => updateGuideFn({ data: { guideId: guide.id, status: s } }),
                  "Estado actualizado",
                )
              }
            >
              {s === "preparing" ? "En producción" : s === "published" ? "Publicada" : "Desactivada"}
            </Button>
          ))}
        </div>
      </section>

      {/* Language matrix */}
      <section className="mt-6 rounded-2xl border border-border bg-card p-5">
        <h2 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">
          Idiomas
        </h2>
        <ul className="mt-4 space-y-2">
          {guide.languages.map((l) => (
            <li
              key={l.id}
              className={`flex flex-wrap items-center gap-3 rounded-xl border px-3 py-2 ${
                l.lang === (lang ?? guideQuery.data?.lang)
                  ? "border-primary/40 bg-primary/5"
                  : "border-border"
              }`}
            >
              <button
                type="button"
                className="text-sm font-semibold text-foreground"
                onClick={() => setLang(l.lang)}
              >
                {LANG_LABELS[l.lang]}
              </button>
              <Badge variant={l.status === "published" ? "default" : "outline"}>{l.status}</Badge>
              <span className="text-xs text-muted-foreground">
                {l.chaptersWithAudio}/{l.chapterCount} con audio ·{" "}
                {formatDuration(l.totalDurationSeconds)}
              </span>
              <div className="ml-auto flex gap-2">
                {l.status !== "published" ? (
                  <Button
                    size="sm"
                    onClick={() =>
                      void run(
                        () => updateLangFn({ data: { languageId: l.id, status: "published" } }),
                        "Idioma publicado",
                      )
                    }
                  >
                    Publicar
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      void run(
                        () => updateLangFn({ data: { languageId: l.id, status: "draft" } }),
                        "Idioma despublicado",
                      )
                    }
                  >
                    Despublicar
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    if (!window.confirm(`¿Eliminar el idioma ${LANG_LABELS[l.lang]}?`)) return;
                    void run(
                      () => deleteLangFn({ data: { languageId: l.id } }),
                      "Idioma eliminado",
                    );
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>

        {missingLangs.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">Agregar idioma:</span>
            {missingLangs.map((l) => (
              <Button
                key={l}
                size="sm"
                variant="outline"
                onClick={() =>
                  void run(
                    () => addLangFn({ data: { guideId: guide.id, lang: l, seedTemplate: true } }),
                    `${LANG_LABELS[l]} agregado con la estructura sugerida`,
                  )
                }
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                {LANG_LABELS[l]}
              </Button>
            ))}
          </div>
        )}
      </section>

      {/* Chapters */}
      {currentLangRow && (
        <section className="mt-6 rounded-2xl border border-border bg-card p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">
              Capítulos · {LANG_LABELS[currentLangRow.lang]}
            </h2>
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                void run(
                  () =>
                    upsertChapterFn({
                      data: {
                        languageId: currentLangRow.id,
                        title: `Capítulo ${chapters.length + 1}`,
                        position: chapters.length + 1,
                      },
                    }),
                  "Capítulo agregado",
                )
              }
            >
              <Plus className="mr-1.5 h-4 w-4" /> Nuevo capítulo
            </Button>
          </div>

          <ul className="mt-4 space-y-4">
            {chapters.map((c, i) => (
              <ChapterCard
                key={c.id}
                chapter={c}
                index={i}
                total={chapters.length}
                busy={busyChapter === c.id}
                previewUrl={preview?.id === c.id ? preview.url : null}
                onSave={(patch) =>
                  run(
                    () =>
                      upsertChapterFn({
                        data: { chapterId: c.id, languageId: currentLangRow.id, ...patch },
                      }),
                    "Capítulo guardado",
                    c.id,
                  )
                }
                onDraft={() =>
                  run(() => scriptFn({ data: { chapterId: c.id } }), "Borrador generado", c.id)
                }
                onSynth={() =>
                  run(
                    () => audioFn({ data: { chapterId: c.id } }),
                    "Audio generado y guardado",
                    c.id,
                  )
                }
                onDeleteAudio={() =>
                  run(
                    () => deleteAudioFn({ data: { chapterId: c.id } }),
                    "Audio eliminado",
                    c.id,
                  )
                }
                onDelete={() => {
                  if (!window.confirm("¿Eliminar este capítulo?")) return;
                  void run(
                    () => deleteChapterFn({ data: { chapterId: c.id } }),
                    "Capítulo eliminado",
                    c.id,
                  );
                }}
                onMove={(dir) => {
                  const next = [...chapters];
                  const target = dir === "up" ? i - 1 : i + 1;
                  if (target < 0 || target >= next.length) return;
                  const a = next[i]!;
                  const b = next[target]!;
                  next[i] = b;
                  next[target] = a;
                  void run(
                    () =>
                      reorderFn({
                        data: {
                          languageId: currentLangRow.id,
                          order: next.map((ch, idx) => ({ chapterId: ch.id, position: idx + 1 })),
                        },
                      }),
                    "Orden actualizado",
                    c.id,
                  );
                }}
                onPreview={async () => {
                  setBusyChapter(c.id);
                  try {
                    const res = await urlFn({ data: { chapterId: c.id } });
                    if (res.url) setPreview({ id: c.id, url: res.url });
                    else fail(new Error("Este capítulo aún no tiene audio"));
                  } catch (e) {
                    fail(e);
                  } finally {
                    setBusyChapter(null);
                  }
                }}
                onUpload={async (file) => {
                  setBusyChapter(c.id);
                  try {
                    const buf = new Uint8Array(await file.arrayBuffer());
                    let binary = "";
                    for (let k = 0; k < buf.length; k += 8192) {
                      binary += String.fromCharCode(...buf.subarray(k, k + 8192));
                    }
                    const contentType =
                      file.type === "audio/wav" || file.type === "audio/ogg"
                        ? file.type
                        : "audio/mpeg";
                    await uploadFn({
                      data: { chapterId: c.id, content: btoa(binary), contentType },
                    });
                    ok("Audio subido");
                    await refresh();
                  } catch (e) {
                    fail(e);
                  } finally {
                    setBusyChapter(null);
                  }
                }}
              />
            ))}
            {chapters.length === 0 && (
              <li className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                Este idioma no tiene capítulos todavía.
              </li>
            )}
          </ul>
        </section>
      )}
    </main>
  );
}

interface ChapterCardProps {
  chapter: AdminChapter;
  index: number;
  total: number;
  busy: boolean;
  previewUrl: string | null;
  onSave: (patch: {
    title: string;
    script?: string | null;
    status?: "draft" | "review" | "published" | "disabled";
  }) => void;
  onDraft: () => void;
  onSynth: () => void;
  onDelete: () => void;
  onDeleteAudio: () => void;
  onMove: (dir: "up" | "down") => void;
  onPreview: () => void;
  onUpload: (file: File) => void;
}

function ChapterCard({
  chapter,
  index,
  total,
  busy,
  previewUrl,
  onSave,
  onDraft,
  onSynth,
  onDelete,
  onDeleteAudio,
  onMove,
  onPreview,
  onUpload,
}: ChapterCardProps) {
  const [title, setTitle] = useState(chapter.title);
  const [script, setScript] = useState(chapter.script ?? "");

  useEffect(() => {
    setTitle(chapter.title);
    setScript(chapter.script ?? "");
  }, [chapter.id, chapter.title, chapter.script]);

  return (
    <li className="rounded-2xl border border-border p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-xs font-bold">
          {String(chapter.position).padStart(2, "0")}
        </span>
        <Input
          className="min-w-[200px] flex-1"
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
        />
        <Badge variant={chapter.hasAudio ? "default" : "outline"}>
          {chapter.hasAudio ? formatDuration(chapter.durationSeconds) : "sin audio"}
        </Badge>
        {chapter.aiGenerated && <Badge variant="secondary">borrador IA</Badge>}
        <Badge variant="outline">{chapter.status}</Badge>
        <div className="ml-auto flex gap-1">
          <Button
            size="icon"
            variant="ghost"
            disabled={index === 0 || busy}
            aria-label="Subir"
            onClick={() => onMove("up")}
          >
            <ChevronUp className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            disabled={index + 1 >= total || busy}
            aria-label="Bajar"
            onClick={() => onMove("down")}
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="ghost" aria-label="Eliminar" onClick={onDelete}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Textarea
        className="mt-3"
        rows={6}
        placeholder="Guion narrado de este capítulo…"
        value={script}
        onChange={(e) => setScript(e.currentTarget.value)}
      />

      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" disabled={busy} onClick={() => onSave({ title, script })}>
          {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
          Guardar
        </Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={onDraft}>
          <Sparkles className="mr-1.5 h-4 w-4" /> Borrador con IA
        </Button>
        <Button size="sm" variant="outline" disabled={busy || !script.trim()} onClick={onSynth}>
          <Mic className="mr-1.5 h-4 w-4" /> Generar audio
        </Button>
        {chapter.hasAudio && (
          <>
            <Button size="sm" variant="ghost" disabled={busy} onClick={onPreview}>
              <Volume2 className="mr-1.5 h-4 w-4" /> Escuchar
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={onDeleteAudio}>
              <Trash2 className="mr-1.5 h-4 w-4" /> Quitar audio
            </Button>
          </>
        )}
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-medium hover:bg-accent">
          <Upload className="h-4 w-4" /> Subir MP3
          <input
            type="file"
            accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg"
            className="hidden"
            onChange={(e) => {
              const f = e.currentTarget.files?.[0];
              if (f) onUpload(f);
              e.currentTarget.value = "";
            }}
          />
        </label>
        <select
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          value={chapter.status}
          onChange={(e) =>
            onSave({
              title,
              script,
              status: e.currentTarget.value as "draft" | "review" | "published" | "disabled",
            })
          }
        >
          {["draft", "review", "published", "disabled"].map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {previewUrl && (
        <audio className="mt-3 w-full" controls src={previewUrl} preload="none" />
      )}
    </li>
  );
}
